'use client';
import { Flex, omitText, DsmColor, Typography, Box, Coachmark, NotificationDot, CartBadge } from '@kohler-dsm-library/kohler-core';
import { UserIcon, FavoriteNavFilledIcon, FavoriteNavIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { LanguageMenu } from '../LanguageMenu/LanguageMenu.mjs';
import { LoginMenu } from '../LoginMenu/LoginMenu.mjs';
import { MenuWrapper } from '../MenuWrapper/MenuWrapper.mjs';
import { StoreMenu } from '../StoreMenu/StoreMenu.mjs';
import { LocationPickButton } from './subcomponents/LocationPickButton/LocationPickButton.mjs';
import { LanguagePicker } from './subcomponents/LanguagePicker/LanguagePicker.mjs';
import classes from './UtilityLinks.module.css.mjs';

const omitFavoritesCTA = (favoritesCTA) => {
  const { isFilled = false, notificationDot, ...omitFavoritesCTA2 } = favoritesCTA;
  return { isFilled, notificationDot, omitFavoritesCTA: omitFavoritesCTA2 };
};
const UtilityLinks = ({
  isLoggedIn = false,
  locationPickerCTA,
  languagePicker,
  favoritesCTA,
  cartBadgeCTA,
  userName,
  userCTA,
  utilityLinksDesktop,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  utilityLinksMobile,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userCTAMobile,
  menuType,
  storeMenu,
  loginMenu,
  languageMenu,
  setMenuType,
  setIsOpenMobileMenu,
  className,
  ...rest
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuType?.(null);
        setIsOpenMobileMenu?.(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setMenuType, setIsOpenMobileMenu]);
  return /* @__PURE__ */ React.createElement(Flex, { className: clsx(classes.utilityLinks, className), gap: 20, ...rest }, (locationPickerCTA || languagePicker || utilityLinksDesktop) && /* @__PURE__ */ React.createElement(Flex, { align: "center" }, /* @__PURE__ */ React.createElement(Flex, { align: "center", gap: 25, paddingLeft: 25 }, utilityLinksDesktop?.length && utilityLinksDesktop.map((utilityLink) => {
    const { icon, ...linkProps } = utilityLink;
    return /* @__PURE__ */ React.createElement(
      Flex,
      {
        key: utilityLink.text,
        component: "a",
        align: "center",
        gap: 10,
        textColor: DsmColor.ContentPrimary,
        ...omitText(linkProps)
      },
      icon,
      /* @__PURE__ */ React.createElement(Typography, { size: "label-medium-s", component: "span", textColor: DsmColor.ContentPrimary }, utilityLink.text)
    );
  }), locationPickerCTA && /* @__PURE__ */ React.createElement(LocationPickButton, { "aria-expanded": menuType === "store", "aria-controls": "store-menu", ...locationPickerCTA }), storeMenu && /* @__PURE__ */ React.createElement(MenuWrapper, { menuType, currentType: "store", setMenuType, isOpen: menuType === "store" }, /* @__PURE__ */ React.createElement(StoreMenu, { ...storeMenu, setMenuType, setIsOpenMobileMenu })), languagePicker && /* @__PURE__ */ React.createElement(
    LanguagePicker,
    {
      ...languagePicker,
      languagePickerCTA: {
        "aria-controls": "language-menu",
        "aria-expanded": menuType === "language",
        ...languagePicker.languagePickerCTA
      }
    }
  ), languageMenu && languageMenu.regions && languageMenu.regions.length > 0 && /* @__PURE__ */ React.createElement(MenuWrapper, { menuType, currentType: "language", setMenuType, isOpen: menuType === "language" }, /* @__PURE__ */ React.createElement(LanguageMenu, { ...languageMenu, setMenuType, setIsOpenMobileMenu }))), (locationPickerCTA || languagePicker) && /* @__PURE__ */ React.createElement("div", { className: classes.divider })), /* @__PURE__ */ React.createElement(Flex, { className: classes.utilityLink, gap: 15, align: "center" }, userCTA && /* @__PURE__ */ React.createElement(
    Box,
    {
      component: "button",
      type: "button",
      display: "flex",
      textColor: DsmColor.ContentPrimary,
      "aria-label": isLoggedIn ? "user sign in" : "profile",
      "aria-expanded": menuType === "user",
      "aria-controls": "login-menu",
      ...omitText(userCTA)
    },
    isLoggedIn && userName ? /* @__PURE__ */ React.createElement(Typography, { size: "label-medium-xs", textColor: DsmColor.ContentPrimary }, "Hi, ", userName) : /* @__PURE__ */ React.createElement(UserIcon, { withBoundingBox: false })
  ), loginMenu && /* @__PURE__ */ React.createElement(MenuWrapper, { menuType, currentType: "user", setMenuType, isOpen: menuType === "user" }, /* @__PURE__ */ React.createElement(LoginMenu, { ...loginMenu, setMenuType, setIsOpenMobileMenu })), favoritesCTA && /* @__PURE__ */ React.createElement(
    Coachmark,
    {
      target: /* @__PURE__ */ React.createElement(NotificationDot, { disabled: true, position: "top-end", top: 7, right: 3, withBorder: true, ...favoritesCTA.notificationDot }, /* @__PURE__ */ React.createElement(
        Box,
        {
          component: favoritesCTA?.coachmark?.dropdown && favoritesCTA.notificationDot?.disabled === false ? "span" : "a",
          display: "flex",
          "aria-label": "see favorites",
          textColor: DsmColor.ContentPrimary,
          ...omitFavoritesCTA(favoritesCTA).omitFavoritesCTA
        },
        omitFavoritesCTA(favoritesCTA).isFilled ? /* @__PURE__ */ React.createElement(FavoriteNavFilledIcon, { withBoundingBox: false }) : /* @__PURE__ */ React.createElement(FavoriteNavIcon, { withBoundingBox: false })
      )),
      disabled: !favoritesCTA.coachmark?.dropdown,
      variant: "highlight",
      size: "small",
      position: "bottom",
      withArrow: true,
      ...favoritesCTA.coachmark
    }
  ), cartBadgeCTA && /* @__PURE__ */ React.createElement(
    CartBadge,
    {
      display: "flex",
      textColor: DsmColor.ContentPrimary,
      component: cartBadgeCTA.href ? "a" : "button",
      "aria-label": "see cart",
      withBoundingBox: false,
      ...cartBadgeCTA
    }
  )));
};

export { UtilityLinks };
